import type Projection from 'ol/proj/Projection';

import { getSpatialConditionValues } from 'cadenza/api-client/workbook-api';
import { Command } from 'cadenza/api-client/workbook-commands';
import type { ConditionElement } from 'cadenza/conditions/condition-element';
import { getStore } from 'cadenza/store/cadenza-store';
import type { SpatialFilterRelation } from 'cadenza/workbook/conditions/spatial-condition/model/spatial-filter-relation.gen';
import type {
  SpatialConditionValueDto,
  SpatialFilterGeometryId,
  WorkbookSpatialConditionValueItemDto,
} from 'cadenza/workbook/model/condition-dto';
import type { ConditionId } from 'cadenza/workbook/model/condition-id.gen';
import { ConditionSettingsKey } from 'cadenza/workbook/model/condition-settings-key.gen';
import { executeCommand } from 'cadenza/workbook/workbook-core-reducers.state';
import { getWorkbookNameAndHash } from 'cadenza/workbook/workbook-core-selectors.state';

/**
 * Gets the condition values from endpoint and then prepares list of shapes to initialize edit
 * dialog's shapes based on the values and the map SRS. If map SRS is the same as values SRS
 * returned from endpoint, then the shaped can be used as they are. If SRSs are different, then
 * circles and rectangles cannot be used as such, but they are converted to polygons.
 *
 * @param conditionId - Id of the spatial condition
 * @param spatialConditionValues - Optional existing spatial condition values that will
 * be added to the condition
 * @param mapProjection - SRS of the map used SpatialConditionModal
 * @return List of shapes to be used as model for SpatialConditionModal
 */
export function getShapesList(
  conditionId: ConditionId,
  spatialConditionValues: WorkbookSpatialConditionValueItemDto | undefined,
  mapProjection: Projection,
) {
  return Promise.all([getConditionValues(conditionId), import('cadenza/map/shape-picker/shape-picker-utils')]).then(
    ([conditionValues, { convertConditionValueToShape }]) => {
      let loadedConditionValues = conditionValues.values.map((value) => {
        return convertConditionValueToShape(value, conditionValues.srsString, mapProjection);
      });
      if (spatialConditionValues) {
        loadedConditionValues = loadedConditionValues.concat(
          spatialConditionValues.values.map((value) => {
            return convertConditionValueToShape(value, spatialConditionValues.srsString, mapProjection);
          }),
        );
      }
      return loadedConditionValues;
    },
  );
}

/**
 * Gets the condition values from endpoint
 *
 * @param conditionId - Condition element from analysis context
 * @return Spatial condition value
 */
function getConditionValues(conditionId: ConditionId) {
  const workbookNameAndHash = getWorkbookNameAndHash(getStore().getState());
  return getSpatialConditionValues(workbookNameAndHash, conditionId);
}

function assignShapesToGroups(shapes: (SpatialConditionValueDto & { destinationGroupName?: string })[]) {
  for (const shape of shapes) {
    const { destinationGroupName, geometryType } = shape;
    if (destinationGroupName) {
      const groupGeometryType = getExistingGroupGeometryType(shapes, destinationGroupName);
      if (!groupGeometryType || groupGeometryType === geometryType) {
        // The group does not exist yet - we can just create it with the name
        // OR The group exists with the same geometry - we want to add the element to the group
        shapes
          .filter((s) => s.destinationGroupName === destinationGroupName)
          .forEach((s) => {
            s.groupName = destinationGroupName;
            delete s.destinationGroupName;
          });
      } else if (groupGeometryType !== geometryType) {
        // The group exists, but with different geometry type - we create a new group adding a number to the name
        const newGroupName = getNextGroupName(shapes, destinationGroupName);
        shapes
          .filter((s) => s.destinationGroupName === destinationGroupName)
          .forEach((s) => {
            s.groupName = newGroupName;
            delete s.destinationGroupName;
          });
      }
    }
  }
}

function getExistingGroupGeometryType(shapes: SpatialConditionValueDto[], groupName: string) {
  const shapesWithGroup = shapes.filter((shape) => shape.groupName === groupName);
  return shapesWithGroup && shapesWithGroup.length ? shapesWithGroup[0].geometryType : null;
}

function getNextGroupName(shapes: SpatialConditionValueDto[], groupName: string) {
  let i = 1;
  let result;
  while (!result) {
    // Find the "groupName (n)" that is not occupied yet
    if (!shapes.some((shape) => shape.groupName === `${groupName} (${i})`)) {
      result = `${groupName} (${i})`;
    }
    i += 1;
  }
  return result;
}

export function updateSpatialCondition(
  conditionId: ConditionId,
  srsString: string,
  shapes: SpatialConditionValueDto[],
  relation: SpatialFilterRelation,
) {
  assignShapesToGroups(shapes);
  const spatialConditionValue = {
    type: 'spatialConditionValue',
    values: shapes,
    srsString,
  } satisfies WorkbookSpatialConditionValueItemDto;

  const setValuesCommand = Command.SET_SPATIAL_CONDITION({
    conditionId,
    spatialConditionValue,
  });

  const setRelationCommand = Command.SET_CONDITION_SETTING({
    conditionId,
    settingName: ConditionSettingsKey.toPersistenceKey('SPATIAL_CONDITION_RELATION'),
    settingValue: relation,
  });

  const composite = Command.COMPOSITE_COMMAND({
    commands: [setRelationCommand, setValuesCommand],
  });

  return getStore().dispatch(executeCommand(composite)).unwrap();
}

export function removeGeometryFromSpatialCondition(conditionId: ConditionId, geometryId: SpatialFilterGeometryId) {
  return getStore().dispatch(
    executeCommand(
      Command.REMOVE_GEOMETRY_FROM_SPATIAL_CONDITION({
        conditionId,
        geometryId,
      }),
    ),
  );
}

export function findConditionInAnalysisContext<C extends ConditionElement>(
  parentComponent: Element,
  conditionId: ConditionId,
) {
  return parentComponent.querySelector<C>(`.condition[data-id="${conditionId}"]`);
}
